import dynamic from 'next/dynamic';
import { useMemo, useCallback } from 'react';

import { withUIContext, withUserContext } from 'context';

//* Components
import Image from 'components/common/Image';
import Text from 'components/common/Text';
import Icon from 'components/common/Icon';
import LoginPopup from 'components/common/Popups/LoginPopup';
import Video from 'components/common/Video';

const Pdf = dynamic(() => import('./Pdf'), { ssr: false });

//* Style
import MediaLibraryPopupStyle from './style';

const MediaLibraryPopup = ({ data, openPopup, isLoggedIn }) => {
	// Convert storage URL to API route for PDFs (to handle CORS)
	const getPdfUrl = useCallback((url) => {
		if (!url) return url;
		// If URL contains /storage/, convert it to /api/serve-file/
		if (url.includes('/storage/')) {
			const path = url.split('/storage/')[1];
			return `${process.env.REACT_APP_API_SERVER}serve-file/${path}`;
		}
		return url;
	}, []);

	const File = useMemo(() => {
		switch (data.file.type) {
			case 'video':
				return (
					<Video
						src={data.file.url}
						className={'media-library-popup-image media-library-popup-video'}
					/>
				);
			case 'pdf':
				return <Pdf src={getPdfUrl(data.file.url)} />;
			default:
				return (
					<Image
						objectFit={'cover'}
						src={data.thumbnail.url}
						alt={data.file.alt}
						className={'media-library-popup-image'}
					/>
				);
		}
	}, [data, getPdfUrl]);

	const handleDownload = useCallback(() => {
		const link = `${process.env.REACT_APP_API_SERVER}media/download-file/${data.file.id}`;

		isLoggedIn ? (window.location.href = link) : openPopup(<LoginPopup />, { link });
	}, [data]);

	return (
		<>
			<MediaLibraryPopupStyle fileType={data.file.type} />
			<div className='media-library-popup-styles'>
				{File}
				<div className='media-library-popup-content-wrap'>
					<Text
						className={'pXS font-helvetica uppercase'}
						text={'preview'}
					/>
					<Text
						className={'h6 font-ping-medium media-library-popup-name'}
						text={data.name}
					/>

					<div className='media-library-popup-info-block'>
						<Text
							className={'pS font-ping-medium media-library-info-block-left secondaryColor2'}
							text={'fileType'}
						/>
						<Text
							className={'pS font-ping-medium'}
							text={data.file.extension}
						/>
					</div>
					<div className='media-library-popup-info-block'>
						<Text
							className={'pS font-ping-medium media-library-info-block-left secondaryColor2'}
							text={'category'}
						/>
						<Text
							className={'pS font-ping-medium'}
							text={data.category.name}
						/>
					</div>
					{data.file.dimensions && (
						<div className='media-library-popup-info-block'>
							<Text
								className={'pS font-ping-medium media-library-info-block-left secondaryColor2'}
								text={'dimensions'}
							/>
							<Text
								className={'pS font-ping-medium'}
								text={`${data.file.dimensions}`}
							/>
						</div>
					)}
					<div className='media-library-popup-info-block'>
						<Text
							className={'pS font-ping-medium media-library-info-block-left secondaryColor2'}
							text={'size'}
						/>
						<Text
							className={'pS font-ping-medium'}
							text={data.file.size}
						/>
					</div>
					<a
						onClick={handleDownload}
						rel='noopener noreferrer'
						className='media-library-popup-download-link'>
						<Text
							className={'pS font-ping-bolder'}
							text={'downloadFile'}
						/>
						<Icon
							name={'arrow-medium'}
							className={'download-btn-icon'}
						/>
					</a>
				</div>
			</div>
		</>
	);
};

export default withUIContext(withUserContext(MediaLibraryPopup, ['isLoggedIn']), ['openPopup']);
